/**
 * Skaper-studio – hub for all creator tools.
 * Landing: card-grid of tools. Klikk = enten inline drill (promos/links) eller lenke til full-side.
 *
 * URL: /skaper/studio (?tab=promos|links)
 * Tilgang: kun innloggede skapere
 */
import React, { useState, useEffect } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Sparkles, Tag, ExternalLink as LinkIcon, ArrowLeft, Users, HelpCircle,
  Shield, Share2, Scissors, Wallet, TrendingUp, Zap,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import StudioPromos from "../components/studio/StudioPromos";
import StudioLinks from "../components/studio/StudioLinks";
import CreatorTour from "../components/CreatorTour";
import { STUDIO_TOUR_STEPS } from "../components/tour/steps_studio";

const INLINE_TABS = ["promos", "links"];

export default function SkaperStudio() {
  const { user, loading, refresh } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("tab");
    return INLINE_TABS.includes(p) ? p : "hub";
  });
  const [showTour, setShowTour] = useState(false);

  // Auto-vis dypdykk første gang ELLER hvis ?tour=1
  useEffect(() => {
    if (!user || user === false) return;
    if (!user.is_creator) return;
    const params = new URLSearchParams(window.location.search);
    const force = params.get("tour") === "1";
    const firstTime = user.studio_tour_completed !== true || !localStorage.getItem(`ne_studio_tour_v2_${user.id}`);
    if (force || firstTime) {
      const tm = setTimeout(() => setShowTour(true), 500);
      return () => clearTimeout(tm);
    }
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_creator) return <Navigate to="/" replace />;

  // Verktøy-kort — rekkefølge per bruker 21.07.2026
  const TOOLS = [
    {
      k: "insights",
      icon: TrendingUp,
      title: t("studio_tools.insights_title"),
      desc: t("studio_tools.insights_desc"),
      to: "/skaper/innsikt",
    },
    {
      k: "links",
      icon: LinkIcon,
      title: t("studio_tools.links_title"),
      desc: t("studio_tools.links_desc"),
      inline: true,
    },
    {
      k: "link-page",
      icon: LinkIcon,
      title: t("studio_tools.link_page_title"),
      desc: t("studio_tools.link_page_desc"),
      to: "/skaper/studio/link-side",
    },
    {
      k: "promos",
      icon: Tag,
      title: t("studio_tools.promos_title"),
      desc: t("studio_tools.promos_desc"),
      inline: true,
    },
    {
      k: "social",
      icon: Share2,
      title: t("studio_tools.social_title"),
      desc: t("studio_tools.social_desc"),
      to: "/admin/sosiale-medier",
    },
    {
      k: "collab",
      icon: Users,
      title: t("studio_tools.collab_title"),
      desc: t("studio_tools.collab_desc"),
      to: "/skaper/samarbeid",
    },
    {
      k: "social-subs",
      icon: Wallet,
      title: t("studio_tools.social_subs_title"),
      desc: t("studio_tools.social_subs_desc"),
      to: "/skaper/sosial-abonnenter",
    },
    ...(user?.is_creator || user?.role === "owner" ? [{
      k: "glimt",
      icon: Zap,
      title: t("studio_tools.glimt_title"),
      desc: t("studio_tools.glimt_desc"),
      to: "/skaper/studio/glimt",
    }] : []),
    {
      k: "wheel",
      icon: Sparkles,
      title: t("studio_tools.wheel_title"),
      desc: t("studio_tools.wheel_desc"),
      to: "/skaper/lykkehjul",
    },
    {
      k: "leak",
      icon: Shield,
      title: t("studio_tools.leak_title"),
      desc: t("studio_tools.leak_desc"),
      to: "/leak-sjekk",
    },
    {
      k: "veyl",
      icon: Scissors,
      title: t("studio_tools.veyl_title"),
      desc: t("studio_tools.veyl_desc"),
      to: "/veyl",
    },
  ];

  const handleToolClick = (tool) => {
    if (tool.action) return tool.action();
    if (tool.inline) return setTab(tool.k);
    if (tool.to) return navigate(tool.to);
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="skaper-studio-page">
        <div className="mb-6 flex items-center gap-3">
          <Link
            to="/min-skaperside"
            className="text-[var(--cream-faint)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5"
            data-testid="studio-back"
          >
            <ArrowLeft className="h-4 w-4" /> {t("studio.back_to_page")}
          </Link>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-1">
          <h1 className="font-fraunces italic text-3xl sm:text-4xl text-[var(--cream)]">
            {t("studio.title")}
          </h1>
          <button
            onClick={() => setShowTour(true)}
            data-testid="studio-show-tour-btn"
            className="px-3 py-1.5 border border-white/15 text-[var(--cream-soft)] hover:text-[var(--magenta)] hover:border-[var(--magenta)]/40 font-meta text-xs inline-flex items-center gap-1.5"
          >
            <HelpCircle className="h-3.5 w-3.5" /> {t("tour_studio.show_again")}
          </button>
        </div>
        <p className="text-sm text-[var(--cream-soft)] mb-6">
          {t("studio.subtitle")}
        </p>

        <CreatorExpiryBanner user={user} />

        {/* Breadcrumb / tab-header når inline drill */}
        {tab !== "hub" && (
          <div className="mb-6 flex items-center gap-3">
            <button
              onClick={() => setTab("hub")}
              className="text-[var(--cream-faint)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5 font-meta text-xs"
              data-testid="studio-back-to-hub"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Tilbake til oversikt
            </button>
            <span className="text-[var(--cream-faint)]">·</span>
            <span className="font-fraunces italic text-lg text-[var(--cream)]">
              {TOOLS.find((tt) => tt.k === tab)?.title}
            </span>
          </div>
        )}

        {/* HUB — verktøy-kort */}
        {tab === "hub" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="studio-hub">
            {TOOLS.map((tool) => {
              const Icon = tool.icon;
              return (
                <button
                  key={tool.k}
                  onClick={() => handleToolClick(tool)}
                  className="text-left p-4 border border-white/10 bg-[var(--noir-elev)] hover:border-[var(--magenta)]/50 hover:bg-white/[0.02] transition-colors group"
                  data-testid={`studio-tool-${tool.k}`}
                  data-tour={`studio-${tool.k === "fyp-promo" ? "fyp-promo" : tool.k === "social-subs" ? "social-subs" : tool.k}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 h-10 w-10 flex items-center justify-center border border-[var(--magenta)]/30 bg-[var(--magenta)]/5 group-hover:bg-[var(--magenta)]/15 transition-colors">
                      <Icon className="h-5 w-5 text-[var(--magenta)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-fraunces italic text-lg text-[var(--cream)] leading-tight mb-1">
                        {tool.title}
                      </div>
                      <div className="font-manrope text-xs text-[var(--cream-soft)] leading-relaxed">
                        {tool.desc}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {tab === "promos" && <StudioPromos />}
        {tab === "links"  && <StudioLinks />}

        {showTour && (
          <CreatorTour
            onClose={() => { setShowTour(false); localStorage.setItem(`ne_studio_tour_v2_${user.id}`, "1"); }}
            onComplete={() => { refresh?.(); }}
            completeEndpoint="/me/studio-tour-completed"
            steps={STUDIO_TOUR_STEPS.map((s) => ({
              ...s,
              title: t(s.titleKey),
              body: t(s.bodyKey),
            }))}
          />
        )}
      </div>
    </Layout>
  );
}


function CreatorExpiryBanner({ user }) {
  const until = user?.creator_vip_until;
  if (!until) return null;
  const untilDate = new Date(until);
  if (isNaN(untilDate.getTime())) return null;
  const now = new Date();
  const msLeft = untilDate.getTime() - now.getTime();
  const daysLeft = Math.floor(msLeft / (1000 * 60 * 60 * 24));
  if (msLeft <= 0 || daysLeft > 7) return null;

  const pretty = daysLeft <= 0
    ? "i dag"
    : daysLeft === 1
    ? "i morgen"
    : `om ${daysLeft} dager`;
  const urgent = daysLeft <= 2;

  return (
    <div
      className={`mb-6 px-4 py-3 flex items-center justify-between gap-3 border ${
        urgent
          ? "bg-red-500/15 border-red-500/50 text-red-100"
          : "bg-[var(--magenta)]/10 border-[var(--magenta)]/40 text-[var(--cream)]"
      }`}
      data-testid="creator-expiry-banner"
    >
      <div className="flex-1">
        <div className="font-fraunces italic text-lg">
          Skaper-tilgangen din utløper {pretty}
        </div>
        <div className="text-xs opacity-80 mt-0.5">
          Forny nå for å beholde tilgang til alle 10 verktøy — Glimt, FYP-promo, Skedulerer og resten.
        </div>
      </div>
      <Link
        to="/bli-onlyfans-skaper"
        className={`shrink-0 px-4 py-2 font-bold text-sm uppercase tracking-wider ${
          urgent ? "bg-red-500 text-white" : "bg-[var(--magenta)] text-black"
        }`}
        data-testid="creator-expiry-renew-btn"
      >
        Forny nå
      </Link>
    </div>
  );
}
